import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Building2, GraduationCap, Users, UserRound, BookOpen, Loader2, PenLine,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "./AdminShell";
import { PageHeader } from "./parts";

const CARDS = [
  { key: "schools", label: "Écoles", icon: Building2, color: "text-indigo-600 bg-indigo-50" },
  { key: "teachers", label: "Enseignants", icon: GraduationCap, color: "text-blue-600 bg-blue-50" },
  { key: "parents", label: "Parents", icon: Users, color: "text-violet-600 bg-violet-50" },
  { key: "students", label: "Élèves", icon: UserRound, color: "text-emerald-600 bg-emerald-50" },
  { key: "classes", label: "Classes", icon: BookOpen, color: "text-amber-600 bg-amber-50" },
];

function StatCard({ card, value, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
      data-testid={`stat-card-${card.key}`}
    >
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${card.color}`}>
        <card.icon size={20} />
      </div>
      <div>
        <p className="text-3xl font-semibold tracking-tight text-slate-900" data-testid={`stat-value-${card.key}`}>
          {value ?? 0}
        </p>
        <p className="text-sm text-slate-500">{card.label}</p>
      </div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Impossible de charger les statistiques."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AdminShell>
      <PageHeader title="Tableau de bord" subtitle="Vue d'ensemble de la plateforme." />
      {loading ? (
        <div className="flex justify-center py-24" data-testid="admin-dashboard-loading">
          <Loader2 className="animate-spin text-slate-400" size={28} />
        </div>
      ) : (
        <div className="space-y-6" data-testid="admin-dashboard">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {CARDS.map((c, i) => (
              <StatCard key={c.key} card={c} value={data?.totals?.[c.key]} index={i} />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Taux de signature */}
            <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <PenLine size={20} />
              </div>
              <p className="text-5xl font-semibold tracking-tight text-slate-900" data-testid="signature-rate">
                {data?.signature_rate ?? 0}%
              </p>
              <p className="text-sm text-slate-500">Taux de signature global</p>
              <p className="text-xs text-slate-400">
                {data?.signed_passports ?? 0} / {data?.expected_passports ?? 0} passeports publiés
              </p>
            </div>

            {/* Activité hebdomadaire */}
            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] lg:col-span-2">
              <h3 className="mb-4 text-sm font-semibold text-slate-900">Activité des 8 dernières semaines</h3>
              <div className="h-64" data-testid="weekly-activity-chart">
                {data?.weekly_activity?.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.weekly_activity} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(w) => w.split("-W")[1]} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="publications" name="Publications" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="signatures" name="Signatures" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">
                    Aucune activité pour le moment.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
